import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import axios from 'axios';
import constants from '../../const.js';
import { chatActions } from '../../actions/chat/chatActions.js';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 450px;
  max-width: 90vw;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  font-size: 16px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 32px;
  text-align: center;
`;

const MessageText = styled.div`
  font-size: 14px;
  color: #333;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const MessageAdminButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 32px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MessageUserButton = styled(MessageAdminButton)``;

const LoadingState = styled.div`
  text-align: center;
  color: #999;
  padding: 20px;
  font-size: 13px;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  padding-left: 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UsersList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #f9f9f9;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;

    &:hover {
      background: #999;
    }
  }
`;

const UserItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f0f4ff;
  }

  &:active {
    background: #e8eef5;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserName = styled.div`
  font-weight: 500;
  color: #333;
  font-size: 13px;
`;

const UserEmail = styled.div`
  color: #999;
  font-size: 12px;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #999;
  font-size: 13px;
`;

function NewConversationModal(props) {
  const { onClose, setActiveConversation, getMessages, getConversations, user, perms } = props;
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Check if user is admin
  const isAdmin = user.perm?.some(e => e.role.id === 1);

  useEffect(() => {
    if (isAdmin && searchQuery.trim()) {
      searchUsers(searchQuery);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, isAdmin]);

  const searchUsers = async (query) => {
    setSearching(true);
    try {
      const response = await axios.get(
        `${constants.USER_API.USERS_URL}`,
        {
          headers: { 'Authorization': `${localStorage.getItem('token')}` }
        }
      );
      
      if (response.data && Array.isArray(response.data)) {
        // Filter users based on search query and exclude current user
        const queryLower = query.toLowerCase();
        const filteredUsers = response.data.filter(u => 
          (u.username?.toLowerCase().includes(queryLower) || 
           u.email?.toLowerCase().includes(queryLower)) &&
          u.UID !== user?.UID
        );
        setSearchResults(filteredUsers.slice(0, 10)); // Limit to 10 results
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleStartConversation = async (targetUserId, targetUserName) => {
    setLoading(true);
    try {
      // Create or get conversation with the target user
      const response = await axios.post(
        `${constants.CHAT_SERVER_URL}/conversations`,
        {
          participantIds: [targetUserId],
          isGroupChat: false,
          name: targetUserName || 'User'
        },
        {
          headers: { 'Authorization': `${localStorage.getItem('token')}` }
        }
      );

      if (response.data && response.data.data) {
        const conversationId = response.data.data.id;
        // Refresh conversations list
        await getConversations();
        // Open the conversation
        setActiveConversation(conversationId);
        getMessages(conversationId);
        onClose();
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageAdmin = () => {
    handleStartConversation('1', 'Admin');
  };

  const handleUserSelect = (selectedUser) => {
    handleStartConversation(selectedUser.UID, selectedUser.username);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          New Message
          <CloseButton onClick={onClose}>
            <CloseIcon fontSize="small" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {isAdmin ? (
            <>
              <MessageText>
                Search and select a user to message.
              </MessageText>
              <SearchContainer>
                <SearchIconWrapper>
                  <SearchIcon fontSize="small" />
                </SearchIconWrapper>
                <SearchInput
                  type="text"
                  placeholder="Search by username or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={loading}
                />
              </SearchContainer>

              {searchQuery.trim() && (
                <>
                  {searching ? (
                    <LoadingState>Searching users...</LoadingState>
                  ) : searchResults.length > 0 ? (
                    <UsersList>
                      {searchResults.map((selectedUser) => (
                        <UserItem 
                          key={selectedUser.UID}
                          onClick={() => handleUserSelect(selectedUser)}
                        >
                          <UserAvatar>
                            {selectedUser.username?.charAt(0).toUpperCase() || '?'}
                          </UserAvatar>
                          <UserInfo>
                            <UserName>{selectedUser.username}</UserName>
                            <UserEmail>{selectedUser.email}</UserEmail>
                          </UserInfo>
                        </UserItem>
                      ))}
                    </UsersList>
                  ) : (
                    <EmptyState>No users found matching your search</EmptyState>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <MessageText>
                Need help? Contact our admin team with any questions or concerns.
              </MessageText>
              <MessageAdminButton 
                onClick={handleMessageAdmin}
                disabled={loading}
              >
                {loading ? 'Starting conversation...' : 'Message Admin'}
              </MessageAdminButton>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
}

const mapStateToProps = (state) => {
  return {
    user: state.users.user.user,
    perms: state.users.user.user?.PERM
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setActiveConversation: (conversationId) => dispatch(chatActions.setActiveConversation(conversationId)),
    getMessages: (conversationId) => dispatch(chatActions.getMessages(conversationId)),
    getConversations: () => dispatch(chatActions.getConversations())
  };
};

// Custom comparison for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.user === nextProps.user &&
    prevProps.perms === nextProps.perms &&
    prevProps.onClose === nextProps.onClose &&
    prevProps.setActiveConversation === nextProps.setActiveConversation &&
    prevProps.getMessages === nextProps.getMessages &&
    prevProps.getConversations === nextProps.getConversations
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(React.memo(NewConversationModal, arePropsEqual));
